// Session BBJ: the renewal funnel doors on a call-out and a service record.
//
//   none               "Send them a new contract?" opening the quote calculator tied to the customer and site.
//   pending_signature  an agreement is out for signature: said plainly, no new-quote prompt.
//   covered            (call-out only) the lift is on a live contract: "Start renewal" to that contract's Renewal tab.
//   unconfirmed        the site has a live contract but the lift is not provably on it: "Check this lift is on the
//                      contract", with links to the contract (or the site, when it holds several) and to a new quote.
//
// This component starts nothing and writes nothing. `liftContract` is derived live by the server (never stored);
// `undefined` means an older server that did not send it, which shows nothing rather than a wrong prompt.
import { Link } from "react-router-dom";
import { buttonClasses } from "../../components/Button";

export interface LiftContractInfo {
  state: "covered" | "unconfirmed" | "pending_signature" | "none";
  contractId: string | null;
  activeCount: number;
}

export interface LiftContractDoorProps {
  hasLift: boolean;
  liftContract: LiftContractInfo | null | undefined;
  customerId: string | null;
  siteId: string | null;
  /** Offer the renewal door when the lift is on a contract (the call-out page); the service record does not. */
  showRenewal?: boolean;
}

export function LiftContractDoor({ hasLift, liftContract, customerId, siteId, showRenewal = false }: LiftContractDoorProps) {
  if (!hasLift || !liftContract) return null;
  const quoteLink = (
    <Link
      to="/office/quotes/new"
      state={{ link: { customerId: customerId ?? undefined, siteId: siteId ?? undefined } }}
      className={buttonClasses("secondary", "sm", "mt-3")}
    >
      Start a new quote
    </Link>
  );
  const several = liftContract.activeCount > 1;
  const contractHref = liftContract.contractId ? `/office/contracts/${encodeURIComponent(liftContract.contractId)}?tab=renewal` : null;
  const severalNote = several ? ` This site holds ${liftContract.activeCount} live contracts.` : "";
  const siteHref = siteId ? `/office/sites/${encodeURIComponent(siteId)}` : null;

  if (liftContract.state === "none") {
    return (
      <section aria-label="Contract status" className="rounded-ele border border-ele-hairline bg-ele-surface p-4">
        <h2 className="font-heading text-lg tracking-wide text-ele-strong">No live contract</h2>
        <p className="mt-1 text-sm text-ele-body">
          This lift is not under a live contract. Send them a new contract? This opens a new quote tied to the same customer and site.
        </p>
        {quoteLink}
      </section>
    );
  }

  if (liftContract.state === "pending_signature") {
    return (
      <section aria-label="Contract status" className="rounded-ele border border-ele-hairline bg-ele-surface p-4">
        <h2 className="font-heading text-lg tracking-wide text-ele-strong">Agreement out for signature</h2>
        <p className="mt-1 text-sm text-ele-body">An agreement for this site is waiting to be signed, so there is no need for a new quote.</p>
      </section>
    );
  }

  if (liftContract.state === "unconfirmed") {
    return (
      <section aria-label="Contract status" className="rounded-ele border border-ele-hairline bg-ele-surface p-4">
        <h2 className="font-heading text-lg tracking-wide text-ele-strong">Check this lift is on the contract</h2>
        <p className="mt-1 text-sm text-ele-body">
          This site has a live contract, but this lift is not confirmed as covered by it.{severalNote}
        </p>
        <div className="flex flex-wrap gap-2">
          {several && siteHref ? (
            <Link to={siteHref} className={buttonClasses("secondary", "sm", "mt-3")}>
              See the site&apos;s contracts
            </Link>
          ) : contractHref ? (
            <Link to={contractHref} className={buttonClasses("secondary", "sm", "mt-3")}>
              Open the contract
            </Link>
          ) : null}
          {quoteLink}
        </div>
      </section>
    );
  }

  if (!showRenewal || !contractHref) return null;
  return (
    <section aria-label="Contract status" className="rounded-ele border border-ele-hairline bg-ele-surface p-4">
      <h2 className="font-heading text-lg tracking-wide text-ele-strong">Under contract</h2>
      <p className="mt-1 text-sm text-ele-body">
        This lift is on a live contract. Renewal starts on the contract, where its lifts are confirmed first and the customer link is issued.
        {severalNote}
      </p>
      <Link to={contractHref} className={buttonClasses("secondary", "sm", "mt-3")}>
        Start renewal
      </Link>
    </section>
  );
}
